import { useEffect, useState } from "react";
import { CheckCircle2, Loader2, ShieldCheck, Coffee, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { Reveal, LineReveal } from "../components/motion";
import CalendlyEmbed from "../components/CalendlyEmbed";
import { api, track } from "../lib/api";
import { useSEO, usePageView } from "../lib/seo";

const EXPECT = [
  { icon: Coffee, title: "A relaxed 15 minutes", body: "A friendly chat about your child's sleep — what's happening, what you've tried, and what you'd love to change. Bring the baby, the coffee, the chaos. All welcome." },
  { icon: HeartHandshake, title: "Honest fit, not a sales pitch", body: "I'll tell you plainly which support (if any) fits your situation. If a $29 guide will do it, I'll say so. If I'm not the right person, I'll point you to who is." },
  { icon: ShieldCheck, title: "Zero obligation", body: "There's nothing to buy on this call and no follow-up pressure. You'll leave with at least one useful idea, whatever you decide." },
];

export default function BookCall() {
  const [form, setForm] = useState({ name: "", email: "", child_age: "", challenge: "", notes: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [qualified, setQualified] = useState(false);
  const [scheduled, setScheduled] = useState(false);

  useSEO({
    title: "Book a Free 15-Minute Suitability Call — The Nanny Sleep Co.",
    description: "A free, no-obligation 15-minute call to see whether gentle sleep support is right for your family. Pick a time that suits you.",
  });
  usePageView("/book-a-call");

  useEffect(() => {
    track("booking_start", { source: "page_view" });
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Please tick the consent box first.");
    setLoading(true);
    try {
      await api.bookingLead({ ...form, notes: form.notes || undefined, consent });
      track("booking_start", { source: "qualification_form", child_age: form.child_age, challenge: form.challenge });
      setQualified(true);
      setTimeout(() => document.getElementById("scheduler")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch {
      toast.error("Couldn't save your details — please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (scheduled) {
    return (
      <section className="max-w-2xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center" data-testid="booking-confirmation">
        <CheckCircle2 className="w-14 h-14 text-sage mx-auto mb-6" strokeWidth={1.5} />
        <h1 className="font-serif font-light text-4xl sm:text-5xl text-ink mb-5">You're booked in.</h1>
        <p className="text-lg text-ink-soft leading-relaxed max-w-md mx-auto mb-8">
          You'll have a confirmation email with the details and a calendar invite. If anything changes, you can reschedule straight from that email — no awkwardness, ever.
        </p>
        <p className="text-sm text-ink-muted">Looking forward to meeting you{form.name ? `, ${form.name.split(" ")[0]}` : ""}. — Tamika</p>
      </section>
    );
  }

  return (
    <div data-testid="book-call-page">
      <section className="py-20 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="overline-label mb-5">Free 15-minute suitability call</p>
          <LineReveal
            className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink"
            lines={["Fifteen minutes.", "Zero obligation."]}
          />
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-3 gap-6">
          {EXPECT.map((e, i) => (
            <Reveal key={e.title} delay={i * 0.08}>
              <div className="card-soft p-7 h-full">
                <e.icon className="w-6 h-6 text-terra mb-4" strokeWidth={1.6} />
                <h2 className="font-serif text-xl font-medium text-ink mb-2.5">{e.title}</h2>
                <p className="text-sm text-ink-soft leading-relaxed">{e.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32" id="scheduler">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <Reveal>
            <div className="card-soft p-7 sm:p-9">
              <h2 className="font-serif text-2xl font-medium text-ink mb-2">First, a little context</h2>
              <p className="text-sm text-ink-muted mb-7">Four quick questions so our 15 minutes counts. Your details carry straight into the calendar.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="booking-qualification-form">
                <div>
                  <label htmlFor="bq-name" className="text-sm font-semibold text-ink block mb-1.5">Your name</label>
                  <input id="bq-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink" />
                </div>
                <div>
                  <label htmlFor="bq-email" className="text-sm font-semibold text-ink block mb-1.5">Email</label>
                  <input id="bq-email" data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink" />
                </div>
                <div>
                  <label htmlFor="bq-age" className="text-sm font-semibold text-ink block mb-1.5">Your child's age</label>
                  <select id="bq-age" data-testid="booking-age-select" required value={form.child_age} onChange={set("child_age")} className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink">
                    <option value="">Choose one…</option>
                    <option value="newborn">Newborn (0–3 months)</option>
                    <option value="baby_4_6">4–6 months</option>
                    <option value="baby_7_18">7–18 months</option>
                    <option value="toddler">Toddler (18 months – 5 years)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="bq-challenge" className="text-sm font-semibold text-ink block mb-1.5">What's hardest right now?</label>
                  <select id="bq-challenge" data-testid="booking-challenge-select" required value={form.challenge} onChange={set("challenge")} className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink">
                    <option value="">Choose one…</option>
                    <option value="naps">Naps are a battle</option>
                    <option value="settling">Settling to sleep</option>
                    <option value="frequent_wakes">Frequent night wakes</option>
                    <option value="bedtime">Bedtime takes forever</option>
                    <option value="early_wakes">Very early morning wakes</option>
                    <option value="routine">No predictable routine</option>
                    <option value="transition">A big change (work, moving, new sibling)</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="bq-notes" className="text-sm font-semibold text-ink block mb-1.5">Anything else? <span className="font-normal text-ink-muted">(optional)</span></label>
                  <textarea id="bq-notes" data-testid="booking-notes-input" rows={3} value={form.notes} onChange={set("notes")} className="w-full px-4 py-3 rounded-xl border border-ink/15 bg-card focus:border-terra focus:outline-none text-ink resize-none" placeholder="Twins, return to work, a specific worry…" />
                </div>
                <label className="flex gap-3 items-start text-sm text-ink-soft cursor-pointer">
                  <input type="checkbox" data-testid="booking-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-[#57705F]" />
                  <span>I consent to The Nanny Sleep Co. using these details to arrange my call. My information is never shared or sold.</span>
                </label>
                <button type="submit" disabled={loading} data-testid="booking-form-submit" className="btn-primary w-full disabled:opacity-70">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {qualified ? "Update my details" : "Continue to the calendar"}
                </button>
              </form>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className={qualified ? "" : "opacity-60 pointer-events-none select-none"} aria-hidden={!qualified}>
              <h2 className="font-serif text-2xl font-medium text-ink mb-2">Then pick your time</h2>
              <p className="text-sm text-ink-muted mb-6">{qualified ? "Choose whatever suits — times shown in your local timezone." : "Fill in the quick form to unlock the calendar."}</p>
              <CalendlyEmbed prefill={{ name: form.name, email: form.email }} onScheduled={() => setScheduled(true)} />
              <p className="mt-5 text-xs text-ink-muted leading-relaxed">
                Need to change your time? Reschedule or cancel via your confirmation email. Consultation cancellations: full refund over 48 hours ahead, 50% within 24–48 hours. Rescheduling within a week is often possible — just ask.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
